import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, Calendar, MessageSquare, ArrowRight, ShoppingBag, FileText, Video } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { useCart } from '../contexts/CartContext';
import { useLanguage } from '../i18n/LanguageContext';
interface OrderConfirmationPageProps {
  onNavigate: (page: string) => void;
}
export function OrderConfirmationPage({
  onNavigate
}: OrderConfirmationPageProps) {
  const {
    mainItem,
    totalPrice
  } = useCart();
  const {
    t
  } = useLanguage();
  // Generate stable order number for this session
  const orderNumber = useMemo(() => 'ORD-' + new Date().getFullYear() + '-' + Math.floor(1000 + Math.random() * 9000), []);
  const orderDate = new Date().toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });
  // Use full cart total, fallback to mainItem price, then default
  const price = totalPrice > 0 ? totalPrice : mainItem ? mainItem.price : 5000;
  const formatPrice = (p: number) => p.toLocaleString('ru-RU') + ' ₽';
  return <div className="w-full bg-slate-50 min-h-screen pb-24 pt-12" data-id="element-23342">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-23343">
        {/* Success Header */}
        <div className="text-center mb-12" data-id="element-23344">
          <motion.div initial={{
          scale: 0,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} transition={{
          type: 'spring',
          stiffness: 200,
          damping: 20
        }} className="w-24 h-24 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-23345">
            <CheckCircle2 className="w-12 h-12 text-emerald-600" data-id="element-23346" />
          </motion.div>

          <motion.h1 initial={{
          y: 20,
          opacity: 0
        }} animate={{
          y: 0,
          opacity: 1
        }} transition={{
          delay: 0.1
        }} className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" data-id="element-23347">
            {t('order.success.title')}
          </motion.h1>

          <motion.p initial={{
          y: 20,
          opacity: 0
        }} animate={{
          y: 0,
          opacity: 1
        }} transition={{
          delay: 0.2
        }} className="text-lg text-slate-600 max-w-xl mx-auto" data-id="element-23348">
            {t('order.success.desc')}
          </motion.p>
        </div>

        <motion.div initial={{
        y: 20,
        opacity: 0
      }} animate={{
        y: 0,
        opacity: 1
      }} transition={{
        delay: 0.3
      }} className="space-y-6" data-id="element-23349">
          {/* Order Details Card */}
          <Card padding="lg" className="border-emerald-100 shadow-lg relative overflow-hidden" data-id="element-23350">
            <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-50 rounded-bl-full -z-10" data-id="element-23351"></div>

            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8 pb-6 border-b border-slate-100" data-id="element-23352">
              <div data-id="element-23353">
                <div className="text-sm text-slate-500 mb-1" data-id="element-23354">
                  {t('order.details.number')}
                </div>
                <div className="text-lg font-bold text-slate-900" data-id="element-23355">
                  {orderNumber}
                </div>
              </div>
              <div className="sm:text-right" data-id="element-23356">
                <div className="text-sm text-slate-500 mb-1" data-id="element-23357">
                  {t('order.details.date')}
                </div>
                <div className="text-lg font-medium text-slate-900" data-id="element-23358">
                  {orderDate}
                </div>
              </div>
            </div>

            <div className="space-y-6" data-id="element-23359">
              <h3 className="font-bold text-slate-900" data-id="element-23360">
                {t('order.details.title')}
              </h3>

              <div className="flex items-start gap-4" data-id="element-23361">
                <div className="w-12 h-12 rounded-xl bg-indigo-50 flex items-center justify-center flex-shrink-0" data-id="element-23362">
                  {mainItem?.type === 'package' ? <ShoppingBag className="w-6 h-6 text-indigo-600" data-id="element-23363" /> : mainItem?.type === 'premium-report' ? <FileText className="w-6 h-6 text-indigo-600" data-id="element-23364" /> : <Video className="w-6 h-6 text-indigo-600" data-id="element-23365" />}
                </div>
                <div className="flex-1 min-w-0" data-id="element-23366">
                  <div className="flex items-center gap-2 mb-1" data-id="element-23367">
                    {mainItem?.badges?.map((badge, i) => <Badge key={i} variant={badge.variant as any} className="text-[10px]" data-id="element-23368">
                        {badge.label}
                      </Badge>) || <Badge variant="pro" className="bg-indigo-100 text-indigo-700 border-none text-[10px]" data-id="element-23369">
                        Консультация
                      </Badge>}
                  </div>
                  <h4 className="font-bold text-slate-900 text-base mb-1" data-id="element-23370">
                    {mainItem?.name || 'Полный разбор натальной карты'}
                  </h4>
                  {mainItem?.astrologer && <div className="text-sm text-slate-500" data-id="element-23371">
                      {t('order.details.astrologer', {
                    name: mainItem.astrologer.name
                  })}
                    </div>}
                </div>
                <div className="font-bold text-slate-900" data-id="element-23372">
                  {formatPrice(price)}
                </div>
              </div>

              <div className="pt-6 border-t border-slate-100 flex justify-between items-center" data-id="element-23373">
                <span className="text-lg font-bold text-slate-900" data-id="element-23374">
                  {t('order.details.total')}
                </span>
                <span className="text-2xl font-bold text-emerald-600" data-id="element-23375">
                  {formatPrice(price)}
                </span>
              </div>
            </div>
          </Card>

          {/* Next Steps */}
          <Card padding="lg" className="bg-indigo-50/50 border-indigo-100" data-id="element-23376">
            <h3 className="font-bold text-slate-900 mb-6 flex items-center gap-2" data-id="element-23377">
              <Calendar className="w-5 h-5 text-indigo-600" data-id="element-23378" />
              {t('order.next.title')}
            </h3>

            <div className="space-y-6 relative before:absolute before:inset-0 before:ml-5 before:-translate-x-px md:before:mx-auto md:before:translate-x-0 before:h-full before:w-0.5 before:bg-gradient-to-b before:from-indigo-200 before:to-transparent" data-id="element-23379">
              {mainItem?.type === 'package' ? <>
                  <div className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active" data-id="element-23380">
                    <div className="flex items-center justify-center w-10 h-10 rounded-full border-4 border-white bg-indigo-600 text-white shadow shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 z-10" data-id="element-23381">
                      1
                    </div>
                    <div className="w-[calc(100%-4rem)] md:w-[calc(50%-2.5rem)] p-4 rounded-xl border border-indigo-100 bg-white shadow-sm" data-id="element-23382">
                      <div className="font-bold text-slate-900 mb-1 text-sm" data-id="element-23383">
                        {t('order.next.pkg.1.title')}
                      </div>
                      <div className="text-slate-600 text-xs leading-relaxed" data-id="element-23384">
                        {t('order.next.pkg.1.desc')}
                      </div>
                    </div>
                  </div>
                  <div className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group" data-id="element-23385">
                    <div className="flex items-center justify-center w-10 h-10 rounded-full border-4 border-white bg-indigo-200 text-indigo-700 shadow shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 z-10" data-id="element-23386">
                      2
                    </div>
                    <div className="w-[calc(100%-4rem)] md:w-[calc(50%-2.5rem)] p-4 rounded-xl border border-slate-100 bg-white shadow-sm" data-id="element-23387">
                      <div className="font-bold text-slate-900 mb-1 text-sm" data-id="element-23388">
                        {t('order.next.pkg.2.title')}
                      </div>
                      <div className="text-slate-600 text-xs leading-relaxed" data-id="element-23389">
                        {t('order.next.pkg.2.desc')}
                      </div>
                    </div>
                  </div>
                </> : mainItem?.type === 'premium-report' ? <>
                  <div className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active" data-id="element-23390">
                    <div className="flex items-center justify-center w-10 h-10 rounded-full border-4 border-white bg-indigo-600 text-white shadow shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 z-10" data-id="element-23391">
                      1
                    </div>
                    <div className="w-[calc(100%-4rem)] md:w-[calc(50%-2.5rem)] p-4 rounded-xl border border-indigo-100 bg-white shadow-sm" data-id="element-23392">
                      <div className="font-bold text-slate-900 mb-1 text-sm" data-id="element-23393">
                        {t('order.next.report.1.title')}
                      </div>
                      <div className="text-slate-600 text-xs leading-relaxed" data-id="element-23394">
                        {t('order.next.report.1.desc')}
                      </div>
                    </div>
                  </div>
                  <div className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group" data-id="element-23395">
                    <div className="flex items-center justify-center w-10 h-10 rounded-full border-4 border-white bg-indigo-200 text-indigo-700 shadow shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 z-10" data-id="element-23396">
                      2
                    </div>
                    <div className="w-[calc(100%-4rem)] md:w-[calc(50%-2.5rem)] p-4 rounded-xl border border-slate-100 bg-white shadow-sm" data-id="element-23397">
                      <div className="font-bold text-slate-900 mb-1 text-sm" data-id="element-23398">
                        {t('order.next.report.2.title')}
                      </div>
                      <div className="text-slate-600 text-xs leading-relaxed" data-id="element-23399">
                        {t('order.next.report.2.desc')}
                      </div>
                    </div>
                  </div>
                </> : <>
                  <div className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active" data-id="element-23400">
                    <div className="flex items-center justify-center w-10 h-10 rounded-full border-4 border-white bg-indigo-600 text-white shadow shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 z-10" data-id="element-23401">
                      1
                    </div>
                    <div className="w-[calc(100%-4rem)] md:w-[calc(50%-2.5rem)] p-4 rounded-xl border border-indigo-100 bg-white shadow-sm" data-id="element-23402">
                      <div className="font-bold text-slate-900 mb-1 text-sm" data-id="element-23403">
                        {t('order.next.consult.1.title')}
                      </div>
                      <div className="text-slate-600 text-xs leading-relaxed" data-id="element-23404">
                        {t('order.next.consult.1.desc', {
                      name: mainItem?.astrologer?.name || 'Елена Смирнова'
                    })}
                      </div>
                    </div>
                  </div>

                  <div className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group" data-id="element-23405">
                    <div className="flex items-center justify-center w-10 h-10 rounded-full border-4 border-white bg-indigo-200 text-indigo-700 shadow shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 z-10" data-id="element-23406">
                      2
                    </div>
                    <div className="w-[calc(100%-4rem)] md:w-[calc(50%-2.5rem)] p-4 rounded-xl border border-slate-100 bg-white shadow-sm" data-id="element-23407">
                      <div className="font-bold text-slate-900 mb-1 text-sm" data-id="element-23408">
                        {t('order.next.consult.2.title')}
                      </div>
                      <div className="text-slate-600 text-xs leading-relaxed" data-id="element-23409">
                        {t('order.next.consult.2.desc')}
                      </div>
                    </div>
                  </div>
                </>}
            </div>
          </Card>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-4 pt-4" data-id="element-23410">
            <Button size="lg" className="flex-1" onClick={() => onNavigate('dashboard-user')} icon={<ArrowRight className="w-4 h-4" data-id="element-23412" />} data-id="element-23411">
              {t('order.btn.dashboard')}
            </Button>
            <Button variant="outline" size="lg" className="flex-1" onClick={() => onNavigate('marketplace')} icon={<ShoppingBag className="w-4 h-4" data-id="element-23414" />} data-id="element-23413">
              {t('order.btn.catalog')}
            </Button>
          </div>
        </motion.div>
      </div>
    </div>;
}